// contract-manager/client/pages/assets/edit.jsx
import { ContractSearcher } from '@/components/contract-searcher'
import { ContractSearcherContext } from '@/components/contract-searcher/context'
import { StatusSwitcher } from '@/components/status-switcher'
import { GroupInput } from '@/components/v2/group-input'
import { useAuthorization } from '@/hooks'
import { PageLayout } from '@/layouts/page-layout'
import { list } from '@/router/utils'
import { confirm } from '@/shared/components/confirmation/confirmation.util'
import { useGetAssetQuery, useUpdateAssetMutation } from '@/store/services'
import { Button, Input, Typography } from '@material-tailwind/react'
import { HasAccess } from '@permify/react-role'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigation, useParams } from 'react-router-dom'
import { NotificationModal } from '@/components/notification-modal'

export const AssetEdit = () => {
  const { id } = useParams()
  const { state } = useNavigation()
  const { data: { asset = {} } = {} } = useGetAssetQuery(id)
  const [updateAsset, { isLoading }] = useUpdateAssetMutation()
  const { hasAccess } = useAuthorization({ redirection: { enable: true } })
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false)
  const [notificationIcon, setNotificationIcon] = useState('')
  const [notificationTitle, setNotificationTitle] = useState('')
  const [notificationMessage, setNotificationMessage] = useState('')

  const initialValues = {
    id: asset.id,
    contractor_contract_id: asset.contract?.id,
    accreditation_date: asset.accreditationDate,
    expiration_date: asset.expirationDate,
    status: asset.status,
    vehicle_info: {
      type: asset.vehicleInfo?.type || '',
      patent: asset.vehicleInfo?.patent || '',
      brand: asset.vehicleInfo?.brand || '',
      model: asset.vehicleInfo?.model || '',
      year: asset.vehicleInfo?.year || '',
      color: asset.vehicleInfo?.color || ''
    }
  }

  const currentContract = asset?.contract || {};

  const { t } = useTranslation()

  const handleSubmit = async (values) => {
    try {
      await updateAsset({ id: values.id, ...values }).unwrap()
      
      setNotificationIcon('check')
      setNotificationTitle(t('asset.notifications.success_update', { what: t('asset.one') }))
      setNotificationMessage(t('asset.notifications.update.success'))
      setShowNotification(true)
    } catch (error) {
      const errors = error.data?.errors || []
      
      const bulletList = errors.map(e => `• ${e}`).join('\n')
      const formattedMessage = errors.length > 1 
        ? `${t('errors.following_issues')}\n${bulletList}`
        : errors[0] || t('errors.generic')

      setNotificationIcon('error')
      setNotificationTitle(t('asset.notifications.error_update', { what: t('asset.one') }))
      setNotificationMessage(formattedMessage)
      setShowNotification(true)
    }
  }

  const handleSwitchStatus = async () => {
    const statusConfirmModal = confirm({
      title: t(`modal.confirm.status.${asset.status}.title`),
      description: t(`modal.confirm.status.${asset.status}.message`)
    })

    if (await statusConfirmModal) {
      try {
        await updateAsset({ 
          id: asset.id, 
          status: asset.status === 'active' ? 'inactive' : 'active'
        }).unwrap()
      } catch (error) {
        const errorMsg = error.data?.errors?.join(', ') || t('errors.generic')
        alert(`Error: ${errorMsg}`)
      }
    }
  }

  const handleSwitchAccredited = async () => {
    const accreditedConfirmModal = confirm({
      title: t(`modal.confirm.accredited.${asset.status}.title`),
      description: t(`modal.confirm.accredited.${asset.status}.message`)
    })

    if (await accreditedConfirmModal) {
      try {
        await updateAsset({ 
          id: asset.id,
          accredited: asset.accredited === 'active' ? 'inactive' : 'active'
        }).unwrap()
      } catch (error) {
        const errorMsg = error.data?.errors?.join(', ') || t('errors.generic')
        alert(`Error: ${errorMsg}`)
      }
    }
  }

  useEffect(() => {
    hasAccess('asset#edit')
  }, [])

  return (
    <PageLayout
      title={t('actions.edit.with', { what: t('asset.one').toLowerCase() })}
      editMode
      rightSection={
        <>
          <HasAccess permissions='asset#activate'>
            <StatusSwitcher
              label={'asset'}
              type={'status'}
              value={asset.status}
              onChange={handleSwitchStatus}
            />
          </HasAccess>
          <HasAccess permissions='asset#accredit'>
            <StatusSwitcher
              label={'asset'}
              type={'accredited'}
              value={asset.accredited}
              onChange={handleSwitchAccredited}
            />
          </HasAccess>
        </>
      }
    >
      {showNotification && (
        <NotificationModal
          iconType={notificationIcon}
          title={notificationTitle}
          message={notificationMessage}
          returnPath={notificationIcon === 'check' ? list('assets') : undefined}
          setBackStateFunction={() => setShowNotification(false)}
        />
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ handleChange, values, setFieldValue }) => (
          <Form>
            <GroupInput
              title={t('common.overall')}
              description='Rellena la información general del nuevo contrato.'
              className='py-8'
              divider={false}
            >
              <div className='grid grid-cols-2 gap-4'>
                <ContractSearcherContext.Provider
                  value={{ currentContract }}
                >
                  <ContractSearcher required name='contractor_contract_id' />
                </ContractSearcherContext.Provider>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <Input
                  name='accreditation_date'
                  label={t('common.accreditationDate')}
                  type='date'
                  value={values.accreditation_date}
                  onChange={handleChange}
                />
                <Input
                  name='expiration_date'
                  label={t('common.expirationDate')}
                  type='date'
                  value={values.expiration_date}
                  onChange={handleChange}
                />
              </div>

              <section className='bg-gray-100 px-8 py-6 rounded-2xl grid grid-rows-1 gap-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='col-span-2'>
                    <Typography variant='h6' className='text-lg'>
                      Datos vehículo
                    </Typography>
                    <Typography variant='small' className='text-gray-500'>
                      Rellena los siguientes campos con la información del vehículo.
                    </Typography>
                  </div>

                  <Input
                    required
                    name='vehicle_info.type'
                    value={values.vehicle_info.type}
                    onChange={handleChange}
                    label='Tipo de activo 1.0'
                  />
                  <Input
                    name='vehicle_info.patent'
                    value={values.vehicle_info.patent}
                    onChange={handleChange}
                    label='Patente'
                  />
                  <Input
                    required
                    name='vehicle_info.brand'
                    value={values.vehicle_info.brand}
                    onChange={handleChange}
                    label='Marca'
                  />
                  <Input
                    required
                    name='vehicle_info.model'
                    value={values.vehicle_info.model}
                    onChange={handleChange}
                    label='Modelo'
                  />
                  <Input
                    name='vehicle_info.year'
                    value={values.vehicle_info.year}
                    onChange={handleChange}
                    label='Año'
                  />
                  <Input
                    name='vehicle_info.color'
                    value={values.vehicle_info.color}
                    onChange={handleChange}
                    label='Color'
                  />
                </div>
              </section>
            </GroupInput>

            <div className='flex justify-end gap-3 mt-10'>
              <Link to={list('assets')}>
                <Button variant='text'>
                  {t('actions.cancel')}
                </Button>
              </Link>
              <HasAccess permissions='asset#update' >
                <Button type='submit' loading={isLoading}>
                  {t('actions.update.one')}
                </Button>
              </HasAccess>
            </div>
          </Form>
        )}
      </Formik>
    </PageLayout>
  )
}
